<template>
  <div class="tl">
    <Breadcrumbs main="Команды" title="Удалённые команды"/>

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingTeams }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="ID, название, почта или username капитана…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <b-dropdown variant="link" class="tl-drop" no-caret menu-class="tl-drop__menu">
            <template #button-content>
              <span class="tl-drop__btn" :class="{ 'is-on': !!activeGame }">
                <i class="fa fa-gamepad"></i>
                <span class="tl-drop__btn-text">{{ activeGame ? activeGame.text : 'Дисциплина' }}</span>
                <i class="fa fa-angle-down"></i>
              </span>
            </template>
            <b-dropdown-item @click="setGame(null)">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !!activeGame }"></i> Все дисциплины
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item v-for="game in games" :key="'g' + game.value" @click="setGame(game.value)">
              <i class="fa fa-check tl-drop__check" :class="{ 'is-hidden': !isSameId(activeGameId, game.value) }"></i>
              {{ game.text }}
            </b-dropdown-item>
          </b-dropdown>

          <div class="tl-spacer"></div>

          <div class="tl-seg" role="group" aria-label="Вид">
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'table' }"
                    @click="setViewMode('table')" v-b-tooltip.hover.bottom="'Таблица'">
              <i class="fa fa-list-ul"></i>
            </button>
            <button type="button" class="tl-seg__btn" :class="{ 'is-on': viewMode === 'grid' }"
                    @click="setViewMode('grid')" v-b-tooltip.hover.bottom="'Карточки'">
              <i class="fa fa-th-large"></i>
            </button>
          </div>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <b-dropdown v-if="viewMode === 'table'" variant="link" class="tl-drop" no-caret
                      right menu-class="tl-drop__menu" toggle-class="p-0">
            <template #button-content>
              <span class="tl-icon-btn" v-b-tooltip.hover.bottom="'Колонки'"><i class="fa fa-columns"></i></span>
            </template>
            <b-dropdown-form class="tl-cols">
              <div class="tl-cols__title">Колонки таблицы</div>
              <b-form-checkbox v-for="col in togglableColumns" :key="'c' + col.key"
                               :checked="visibleCols.includes(col.key)"
                               @change="toggleColumn(col.key)"
                               class="tl-cols__item">
                {{ col.label }}
              </b-form-checkbox>
              <button type="button" class="tl-cols__reset" @click="resetColumns">Сбросить</button>
            </b-dropdown-form>
          </b-dropdown>

          <router-link :to="{ name: 'teams' }" class="tl-icon-btn"
                       v-b-tooltip.hover.bottom="'Действующие команды'">
            <i class="fa fa-users"></i>
          </router-link>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingTeams }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>
        </div>

        <div v-if="hasAnyRefine" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>

          <button v-if="searchApplied" type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> «{{ searchApplied }}» <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button v-if="activeGame" type="button" class="tl-chip" @click="setGame(null)">
            <i class="fa fa-gamepad"></i> {{ activeGame.text }} <i class="fa fa-times tl-chip__x"></i>
          </button>

          <button type="button" class="tl-chips__reset" @click="resetAll">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <div v-if="isLoadingTeams" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-archive"></i></div>
          <div class="tl-empty__title">
            {{ hasAnyRefine ? 'Ничего не нашлось' : 'Удалённых команд нет' }}
          </div>
          <div class="tl-empty__text">
            {{ hasAnyRefine
              ? 'Попробуйте изменить условия поиска или сбросить фильтры.'
              : 'Сюда попадают команды, удалённые капитаном или администратором.' }}
          </div>
          <div v-if="hasAnyRefine" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost-2" @click="resetAll">Сбросить фильтры</button>
          </div>
        </div>

        <!-- table -->
        <div v-else-if="viewMode === 'table'" class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="activeFields"
              @row-clicked="openTeam"
          >
            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(name)="{ item }">
              <div class="tl-name">
                <span class="tl-name__logo">{{ initials(item.name) }}</span>
                <button type="button" class="tl-name__title" @click.stop="openTeam(item)" :title="item.name">
                  {{ item.name }}
                </button>
              </div>
            </template>

            <template #cell(owner)="{ item }">
              <span v-if="item.x_owner" class="tl-muted-strong">{{ item.x_owner }}</span>
              <span v-else class="tl-muted">—</span>
            </template>

            <template #cell(members)="{ item }">
              <span class="tl-pill">{{ item.member_count || 0 }}</span>
            </template>

            <template #cell(tournaments)="{ item }">
              <span class="tl-muted-strong">{{ item.tournaments_count || 0 }}</span>
            </template>

            <template #cell(last_tournament)="{ item }">
              <div v-if="item.x_lastTournament" class="tl-time">
                <span class="tl-time__abs">{{ formatDate(item.x_lastTournament) }}</span>
                <span class="tl-time__rel">{{ formatRelative(item.x_lastTournament) }}</span>
              </div>
              <span v-else class="tl-muted">не играла</span>
            </template>

            <template #cell(game)="{ item }">
              <span class="tl-muted-strong">{{ item.x_game || '—' }}</span>
            </template>

            <template #cell(created)="{ item }">
              <div class="tl-time">
                <span class="tl-time__abs">{{ formatDate(item.created_at) }}</span>
                <span class="tl-time__rel">{{ formatRelative(item.created_at) }}</span>
              </div>
            </template>

            <template #cell(deleted)="{ item }">
              <div v-if="item.deleted_at" class="tl-time">
                <span class="tl-time__abs tl-deleted">{{ formatDate(item.deleted_at) }}</span>
                <span class="tl-time__rel">{{ formatRelative(item.deleted_at) }}</span>
              </div>
              <span v-else class="tl-muted">—</span>
            </template>

            <template #cell(action)="{ item }">
              <div class="tl-actions" @click.stop>
                <button type="button" class="tl-act" @click="openTeam(item)"
                        v-b-tooltip.hover.top="'Открыть карточку'">
                  <i class="fa fa-eye"></i>
                </button>
              </div>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <!-- grid -->
        <div v-else class="tl-grid">
          <article v-for="item in rows" :key="'card' + item.id" class="tl-card" @click="openTeam(item)">
            <header class="tl-card__head">
              <span class="tl-card__logo">{{ initials(item.name) }}</span>
              <div class="tl-card__headtext">
                <button type="button" class="tl-card__title" @click.stop="openTeam(item)" :title="item.name">
                  {{ item.name }}
                </button>
                <div class="tl-card__sub">
                  <span class="tl-id tl-id--sm">#{{ item.id }}</span>
                  <span class="tl-dot-sep">·</span>
                  <span>{{ item.x_game || '—' }}</span>
                </div>
              </div>
            </header>

            <div class="tl-card__rows">
              <div class="tl-card__row">
                <span><i class="fa fa-trash-o tl-muted"></i> Удалена</span>
                <span class="tl-deleted">{{ item.deleted_at ? formatDate(item.deleted_at) : '—' }}</span>
              </div>
              <div class="tl-card__row">
                <span><i class="fa fa-user-o tl-muted"></i> Создатель</span>
                <span class="tl-muted-strong">{{ item.x_owner || '—' }}</span>
              </div>
              <div class="tl-card__row">
                <span><i class="fa fa-users tl-muted"></i> Участников</span>
                <span class="tl-muted-strong">{{ item.member_count || 0 }}</span>
              </div>
              <div class="tl-card__row">
                <span><i class="fa fa-trophy tl-muted"></i> Турниров</span>
                <span class="tl-muted-strong">{{ item.tournaments_count || 0 }}</span>
              </div>
            </div>

            <footer class="tl-card__foot" @click.stop>
              <button type="button" class="tl-act" @click="openTeam(item)" v-b-tooltip.hover.top="'Открыть карточку'">
                <i class="fa fa-eye"></i>
              </button>
            </footer>
          </article>
        </div>

        <div v-if="!isLoadingTeams && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ totalDeletedTeams || 0 }}</b>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="currentPageModel"
              :total-rows="totalDeletedTeams"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {TeamsAdapterMixin} from "@/mixins/Teams/TeamsAdapterMixin";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";

const LS = {
  view: "deletedTeamsListView",
  density: "deletedTeamsListDensity",
  columns: "deletedTeamsListColumns",
};

const ALL_COLUMNS = [
  {key: "id", label: "ID", fixed: true},
  {key: "name", label: "Команда", fixed: true},
  {key: "deleted", label: "Удалена"},
  {key: "owner", label: "Создатель"},
  {key: "members", label: "Участников"},
  {key: "tournaments", label: "Турниров"},
  {key: "last_tournament", label: "Последний турнир"},
  {key: "game", label: "Дисциплина"},
  {key: "created", label: "Создана"},
  {key: "action", label: "", fixed: true},
];

const DEFAULT_COLUMNS = ["deleted", "owner", "members", "tournaments", "game", "created"];

export default {
  name: 'DeletedTeamsList',
  mixins: [TeamsAdapterMixin, PaginationHelperMixin],
  data() {
    return {
      pageKey: 'pageNumberDeletedTeamsList',
      perPage: 10,

      searchModel: "",
      searchApplied: "",
      searchTimer: null,

      activeGameId: null,

      viewMode: 'table',
      density: 'cozy',
      visibleCols: [...DEFAULT_COLUMNS],
    }
  },
  computed: {
    activeGame() {
      if (this.activeGameId === null) return null;
      return (this.games || []).find(game => this.isSameId(game.value, this.activeGameId)) || null;
    },

    hasAnyRefine() {
      return Boolean(this.searchApplied) || this.activeGameId !== null;
    },

    rows() {
      return (this.deletedTeams || []).map(team => ({
        ...team,
        x_game: (team.game && team.game.name) || "",
        x_owner: (team.teamOwner && team.teamOwner.email) || "",
        x_lastTournament: (team.lastTournament && team.lastTournament.created_at) || null,
      }));
    },

    togglableColumns() {
      return ALL_COLUMNS.filter(col => !col.fixed);
    },

    activeFields() {
      return ALL_COLUMNS
          .filter(col => col.fixed || this.visibleCols.includes(col.key))
          .map(col => ({
            key: col.key,
            label: col.label,
            sortable: !["action", "id"].includes(col.key),
            thClass: col.key === "action" ? "tl-th--act" : "",
            tdClass: col.key === "action" ? "tl-td--act" : "",
          }));
    },

    currentPageModel: {
      get() {
        return Number(this.deletedTeamsCurrentPage) || 1;
      },
      set(value) {
        this.deletedTeamsCurrentPage = value;
      },
    },

    rangeFrom() {
      if (!this.totalDeletedTeams) return 0;
      return (this.currentPageModel - 1) * this.perPage + 1;
    },

    rangeTo() {
      return Math.min(this.currentPageModel * this.perPage, this.totalDeletedTeams || 0);
    },
  },
  watch: {
    searchModel(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchApplied === value.trim()) return;
        this.searchApplied = value.trim();
        this.deletedTeamsCurrentPage = 1;
        this.fetchTeams();
      }, 400);
    },
  },
  async mounted() {
    this.restorePreferences();
    this.deletedTeamsCurrentPage = Number(this.getCurrentPageNumber(this.pageKey)) || 1;

    await this.fetchTeams();
    await this.grabGamesForTeams();

    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchTeams() {
      this.isLoadingTeams = true;
      this.recordPageToStorage(this.currentPageModel, this.pageKey);
      try {
        await this.trashedTeams({
          pageNumber: this.currentPageModel,
          ...(this.searchApplied && {search: this.searchApplied}),
          ...(this.activeGameId !== null && {gameId: this.activeGameId}),
        });
      } catch (e) {
        this.$toasted.error("Не удалось загрузить удалённые команды", {position: 'top-right', duration: 3000});
      } finally {
        this.isLoadingTeams = false;
      }
    },

    refresh() {
      this.fetchTeams();
    },

    goToPage(pageNumber) {
      this.deletedTeamsCurrentPage = pageNumber;
      this.$nextTick(this.fetchTeams);
    },

    setGame(gameId) {
      this.activeGameId = gameId;
      this.deletedTeamsCurrentPage = 1;
      this.fetchTeams();
    },

    clearSearch() {
      this.searchModel = "";
      if (!this.searchApplied) return;
      this.searchApplied = "";
      this.deletedTeamsCurrentPage = 1;
      this.fetchTeams();
    },

    resetAll() {
      this.searchModel = "";
      this.searchApplied = "";
      this.activeGameId = null;
      this.deletedTeamsCurrentPage = 1;
      this.fetchTeams();
    },

    isSameId(a, b) {
      return a !== null && b !== null && String(a) === String(b);
    },

    openTeam(item) {
      this.showTeamInfo(item.id);
    },

    copyId(id) {
      const done = () => this.$toasted.success(`ID ${id} скопирован`, {position: 'top-right', duration: 1500});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement("textarea");
      area.value = String(id);
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    restorePreferences() {
      try {
        const view = localStorage.getItem(LS.view);
        if (view === "grid" || view === "table") this.viewMode = view;

        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "cozy") this.density = density;

        const columns = JSON.parse(localStorage.getItem(LS.columns) || "null");
        if (Array.isArray(columns)) this.visibleCols = columns;
      } catch (e) { /* повреждённые настройки — берём значения по умолчанию */
      }
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem(LS.view, mode);
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "cozy" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    toggleColumn(key) {
      this.visibleCols = this.visibleCols.includes(key)
          ? this.visibleCols.filter(col => col !== key)
          : [...this.visibleCols, key];
      localStorage.setItem(LS.columns, JSON.stringify(this.visibleCols));
    },

    resetColumns() {
      this.visibleCols = [...DEFAULT_COLUMNS];
      localStorage.setItem(LS.columns, JSON.stringify(this.visibleCols));
    },

    initials(name) {
      if (!name) return "?";
      return String(name).trim().slice(0, 2).toUpperCase();
    },

    formatDate(value) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatRelative(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "";
      const days = Math.round((Date.now() - date.getTime()) / 86400000);
      if (days <= 0) return "сегодня";
      if (days === 1) return "вчера";
      if (days < 30) return `${days} дн. назад`;
      const months = Math.round(days / 30);
      if (months < 12) return `${months} мес. назад`;
      return `${Math.round(months / 12)} г. назад`;
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (isTyping) return;
        if (this.hasAnyRefine) this.resetAll();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";

.tl-name__title,
.tl-card__title {
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.tl-name__logo,
.tl-card__logo {
  font-weight: 700;
  font-size: 12px;
}

.tl-deleted {
  color: var(--tl-danger);
  font-weight: 600;
  font-size: 12.5px;
}
</style>
